/*
Description: This is the file that will handle interacting with the Emerge EMR
Date: 1/16/2026
Author: Christopher Torres

TODO:
    Add Support for Other Question Types (Short Answer, Multiple Choice, Checkbox) 
    Add Support for Advanced SOAP Notes

/**
    Official Functions
*/
export function getPageInfo() {
  /*
          Description: This function will get the page info from the note
          Return: A dictionary with the page info
              {
                  "URL": Base URL for the page,
                  "Note Type": "PT Daily/SOAP Note",...
              }
          */
  const baseURL = window.location.origin;

  // Check if the element exists and get its text content
  let noteType = null;
  let noteTypeElement = null;

  // Find the label that says "Note Template Select"
  const labels = Array.from(document.querySelectorAll('label'));
  noteTypeElement = labels.find(
    (l) => l.textContent.trim() === 'Note Template Select'
  );

  if (noteTypeElement) {
    const inputId = noteTypeElement.getAttribute('for');
    const inputElement = document.getElementById(inputId);

    noteType = inputElement ? inputElement.value.trim() : null;
  }

  if (!noteType) {
    noteType = 'Default SOAP Note';
  }
  expandAllSoapSections();

  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

// function getPatientInfo() {
export function getPatientInfo() {
  /*
    Description: This function will get the patient info from the note
    Return: A dictionary with the patient info
    {
        "EMR ID": 0x1234 or "michalskij"
        "First Name": firstName,
        "Last Name": lastName,
        "DOB": dob, (01/12/1990)
    }
  */

  // This is the element active in the list on the left
  let nameElement = null;

  // Mantine form: First + Last name inputs
  const firstNameLabel = Array.from(document.querySelectorAll('span')).find(
    (span) => span.textContent.trim() === 'First Name'
  );
  const lastNameLabel = Array.from(document.querySelectorAll('span')).find(
    (span) => span.textContent.trim() === 'Last Name'
  );

  if (firstNameLabel && lastNameLabel) {
    const firstInput = firstNameLabel.parentElement.querySelector('input');
    const lastInput = lastNameLabel.parentElement.querySelector('input');

    if (firstInput && lastInput) {
      // Create a synthetic element to preserve template behavior
      nameElement = {
        textContent: firstInput.value + ' ' + lastInput.value
      };
    }
  }

  // If we still can't find it, then we are in the wrong place
  if (!nameElement) {
    console.warn('Unable to extract patient name from the EMR');
    return null;
  }

  const fullName = nameElement.textContent.trim();
  const fullNameArray = fullName.split(' ');
  const firstName = fullNameArray[0];
  const lastName = fullNameArray[fullNameArray.length - 1];

  const emrID = fullName; // Couldn't find an EMR ID on the page so just use the whole name

  // Date of Birth (Mantine DateInput)
  let dob = null;
  const dobLabel = Array.from(document.querySelectorAll('span')).find(
    (span) => span.textContent.trim() === 'Date Of Birth'
  );

  if (dobLabel) {
    const dobInput = dobLabel.parentElement.querySelector(
      "input[data-dates-input='true']"
    );
    if (dobInput && dobInput.value.trim()) {
      dob = dobInput.value.trim();
    }
  }

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

/* 
      questions - This is where we will store the questions
                - a list of dictionaries
           [{
              "Question": "Where was the injury?", 
              "Type": "Multiple Choice", 
              "Choices": ["Back", "Neck", "Shoulder"]
           },
              ...
              ]
      
      */
var questions = [];

/*
      question Interactions - This is where we will store the interactions for each question
                              - a dictionary of dictionaries
               {
                  "Where was the injury?": {
                      "Type": "Multiple Choice",
                      "Choices"{
                          "Back": () => {click the back button},
                          "Neck": () => {click the neck button},
                          "Shoulder": () => {click the shoulder button}
                      }
                  },
              },
              {
                  "What is the injury?": {
                      "Type": "Long Answer",
                      "Input": (val) => {input the value into the input box}
              }
                  ...
               }
      */
var questionInteractions = {};
function normalizeQuestionKey(rawKey) {
  if (!rawKey || typeof rawKey !== 'string') return '';
  // Remove special characters except spaces
  let cleaned = rawKey.replace(/[^\w\s]/g, '');
  // Trim leading and trailing spaces
  cleaned = cleaned.trim();
  return cleaned;
}

export async function getQuestions() {
  // Clear out the old every time.
  questions = [];
  questionInteractions = {};

  const panel = getActiveNotePanel();
  if (!panel) return [];

  const activeTabLabel = document
    .querySelector(
      '.mantine-Tabs-tab[aria-selected="true"] .mantine-Tabs-tabLabel'
    )
    ?.textContent?.trim();

  if (activeTabLabel === 'Doctors Note') {
    expandAllSoapSections();
  }

  // Current Mantine-based EMR UI
  const mantineQuestionElements = panel.querySelectorAll(`
    div.mantine-InputWrapper-root,
    div.bg-white.w-full.rounded-\\[12px\\]
  `);

  mantineQuestionElements.forEach((el) => {
    try {
      const q = getQ(el);
      if (!q || questionInteractions[q.Key]) return;

      // Always register interactions (needed for injection)
      questionInteractions[q.Key] = q;

      // Only expose non–Short Answer questions
      if (q.Type !== 'Short Answer') {
        questions.push(q);
      }
    } catch (e) {
      console.error('Error extracting question:', e);
    }
  });

  return questions;
}

export async function answerQuestions(answers) {
  /*
          Description: This function will take the answers and populate the questions
          
          Args: 
              answers: A dictionary of answers
                  "Question": "The question in question"
                  "Answer": "The answer to the question"
         
          Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
              [
                  {
                      Question: "Where was the injury?", 
                      Answer: "Back" 
                      Error: "The error message" (Choice not found, etc)
                  },
                  ...
              }
      
          */
  // Build the latest question map from the current DOM (supports Mantine UI)
  getQuestions();

  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(answer['Question'], answer['Answer']);
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export async function answerQuestion(question, answer, replyType) {
  /*
          Description: This function will take the answer and populate the question
          
          Args: 
              question: The question in question
              answer: The answer to the question
         
          Return: null if success otherwise a string with the error message
          */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  const normalizedKey = normalizeQuestionKey(question);

  // Make sure the question exists (first try exact, then normalized)
  if (
    questionInteractions[question] == null &&
    questionInteractions[normalizedKey] == null
  ) {
    getQuestions();
    if (
      questionInteractions[question] == null &&
      questionInteractions[normalizedKey] == null
    ) {
      console.error('Question not found');
      console.log(question);
      console.log(questionInteractions);

      return 'Question not found';
    }
  }

  // If it is a Long Answer or long answer, just populate the input
  const qKeyToUse =
    questionInteractions[question] != null ? question : normalizedKey;

  if (questionInteractions[qKeyToUse]['Input']) {
    questionInteractions[qKeyToUse]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[qKeyToUse]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[qKeyToUse]['Choices'][answer[i]](true);
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[qKeyToUse]['Choices']) {
      questionInteractions[qKeyToUse]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.log('Question: ' + question);
      console.log('Answer:' + answer);
      console.log(questionInteractions[qKeyToUse]['Choices']);
      return 'Answer not found';
    }
  }
}

/** Helper Functions */

// Get the active note panel (ie 'Doctors Note' or 'Simple Note')
function getActiveNotePanel() {
  const activeTab = document.querySelector(
    '.mantine-Tabs-tab[aria-selected="true"]'
  );
  if (!activeTab) return null;

  const panelId = activeTab.getAttribute('aria-controls');
  if (!panelId) return null;

  return document.getElementById(panelId);
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

function expandAllSoapSections() {
  // This is the path for the collapsed state of the chevron
  const COLLAPSED_PATH =
    'm12 19.24-4.95-4.95-1.41 1.42L12 22.07l6.36-6.36-1.41-1.42L12 19.24zM5.64 8.29l1.41 1.42L12 4.76l4.95 4.95 1.41-1.42L12 1.93 5.64 8.29z';

  const label = [...document.querySelectorAll('label.cursor-pointer')].find(
    (l) => {
      const svg = l.querySelector('svg');
      const path = svg?.querySelector('path');
      // If the path is the collapsed path, then return true
      return (
        svg?.getAttribute('viewBox') === '0 0 24 24' &&
        path &&
        path.getAttribute('d') === COLLAPSED_PATH
      );
    }
  );

  if (!label) return;

  label.click();
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}

function getQ(questionElement) {
  /**
   * This function will get the question from the question element.
   * @param {HTMLElement} questionElement - The question element
   * @returns {object|null} - The question summary of this type:
   *  {
   *    "Key": "Where was the injury?",
   *    "Question": "Where was the injury?",
   *    "Type": "Short Answer" | "Long Answer",
   *    "Choices": [] | {},
   *    "ExistingNote": "Current saved value on the page",
   *    "Input": func(val) => {input.value = val} // Optional: present for fillable inputs
   *  }
   *  Returns null for non-question elements or unsupported types.
   */

  let questionTitle = null;
  let input = null;
  let qType = null;
  let existingNote = null;

  // Skip section headers
  if (
    (questionElement.querySelector('h1') ||
      questionElement.querySelector('h2')) &&
    !questionElement.querySelector('input, textarea, select')
  ) {
    return null;
  }

  // Question title
  const label = questionElement.querySelector('label');
  if (!label) return null;

  questionTitle = label.textContent.replace(':', '').replace(/\n/g, ' ').trim();

  if (!questionTitle) return null;

  // Text / Number input (Short Answer)
  const numberInput = questionElement.querySelector(
    ':scope input.mantine-NumberInput-input'
  );

  if (numberInput) {
    return {
      Key: normalizeQuestionKey(questionTitle),
      Question: questionTitle,
      Type: 'Short Answer',
      ExistingNote: numberInput.value?.trim() || '',
      Choices: {},
      Input: (val) => {
        if (isEmptyOrNull(val)) return;

        const setter = Object.getOwnPropertyDescriptor(
          HTMLInputElement.prototype,
          'value'
        ).set;
        setter.call(numberInput, val);

        numberInput.dispatchEvent(new Event('input', { bubbles: true }));
        numberInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
  }

  // Textarea (Long Answer)
  const textarea = questionElement.querySelector('textarea');
  if (textarea) {
    input = textarea;
    qType = 'Long Answer';
    existingNote = input.value?.trim() || '';

    return {
      Key: normalizeQuestionKey(questionTitle),
      Question: questionTitle,
      Type: qType,
      ExistingNote: existingNote,
      Choices: [],
      Input: (val) => {
        if (val == null || val === '') return;

        const setter = Object.getOwnPropertyDescriptor(
          HTMLTextAreaElement.prototype,
          'value'
        ).set;
        setter.call(input, val);

        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };
  }

  // Text / Number input (Short Answer)
  const textInput = questionElement.querySelector(
    'input.mantine-NumberInput-input, input[type="text"], input[type="number"]'
  );

  if (textInput) {
    input = textInput;
    qType = 'Short Answer';
    existingNote = input.value?.trim() || '';

    return {
      Key: normalizeQuestionKey(questionTitle),
      Question: questionTitle,
      Type: qType,
      ExistingNote: existingNote,
      Choices: {},
      Input: (val) => {
        if (isEmptyOrNull(val)) return;

        const setter = Object.getOwnPropertyDescriptor(
          HTMLInputElement.prototype,
          'value'
        ).set;
        setter.call(input, val);

        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
  }

  return null;
}
